import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import { Decimal } from 'decimal.js'
import type { DefineUnitInput, RemovedUnit, UnitPriceInput, UnitPrices } from '@/api/analytics'
import type { Minor, Quantity } from '@/domain/money'
import { minorFromMajorString } from '@/domain/money'
import type { UnitDefinition, UnitSeries, UnitSeriesSummary } from '@/domain/units'
import { api, formatQuantity } from '~/api/client'

export interface UnitChartPoint {
  date: string
  /** Recharts plots numbers; `unitsText` is what anything read rather than drawn uses. */
  units: number
  unitsText: Quantity
  amountMinor: Minor
  unitPriceBaseMinor: Minor
}

export interface UnitChange {
  units: number
  amount: number | null
  unitPrice: number | null
}

export function toChartPoints(series: UnitSeries): UnitChartPoint[] {
  return series.points.map((point) => ({
    date: point.date,
    units: Number(point.units),
    unitsText: point.units,
    amountMinor: point.amountMinor,
    unitPriceBaseMinor: point.unitPriceBaseMinor,
  }))
}

export function latestUnits(series: UnitSeries): Quantity | null {
  return series.points[series.points.length - 1]?.units ?? null
}

/**
 * The answer only means anything split in two: wealth moving is the part the
 * owner did, the unit repricing is the part the world did.
 */
export function unitChange(summary: UnitSeriesSummary | undefined): UnitChange | null {
  if (summary?.unitsChangeBp === undefined) return null
  return {
    units: summary.unitsChangeBp,
    amount: summary.amountChangeBp ?? null,
    unitPrice: summary.unitPriceChangeBp ?? null,
  }
}

export function formatUnitCount(quantity: Quantity): string {
  const value = new Decimal(quantity)
  const magnitude = value.abs()
  const places = magnitude.greaterThanOrEqualTo(1000)
    ? 0
    : magnitude.greaterThanOrEqualTo(10)
      ? 1
      : magnitude.greaterThanOrEqualTo(1)
        ? 2
        : 4
  return formatQuantity(value.toDecimalPlaces(places).toFixed())
}

export function sortSeries(series: readonly UnitSeries[]): UnitSeries[] {
  return [...series].sort((a, b) => a.label.localeCompare(b.label))
}

/** A unit with no points cannot be charted, so it must not be the one selected. */
export function firstChartableKey(series: readonly UnitSeries[]): string | null {
  return sortSeries(series).find((s) => s.points.length > 0)?.key ?? null
}

/**
 * Everything a household types to define a unit or to price it, as strings —
 * the price in major units, because nobody enters øre.
 */
export interface UnitDraft {
  label: string
  currency: string
  priceMajor: string
  asOf: string
  source: string
}

export function emptyUnitDraft(baseCurrency: string, today: string): UnitDraft {
  return { label: '', currency: baseCurrency, priceMajor: '', asOf: today, source: '' }
}

/**
 * Why this cannot be saved yet, in the words of whoever would have refused it.
 * The server refuses the same things; a refusal that arrives as a failed request
 * names a field the person can no longer see.
 */
export function refuseUnit(draft: UnitDraft, naming: boolean): string | undefined {
  if (naming && draft.label.trim() === '') return 'a name is required'
  if (naming && draft.currency.trim().length !== 3) return 'a three-letter currency is required'
  if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.asOf)) return 'a date is required, as YYYY-MM-DD'
  if (draft.priceMajor.trim() === '') return 'what one costs is required'
  try {
    if (minorFromMajorString(draft.priceMajor.trim()) <= 0) return 'a price has to be above zero'
  } catch (error) {
    return `price: ${(error as Error).message}`
  }
  return undefined
}

export function toUnitPrice(draft: UnitDraft): UnitPriceInput {
  return {
    priceMinor: minorFromMajorString(draft.priceMajor.trim()),
    asOf: draft.asOf,
    ...(draft.source.trim() === '' ? {} : { source: draft.source.trim() }),
  }
}

export function toDefineUnit(draft: UnitDraft): DefineUnitInput {
  return {
    label: draft.label.trim(),
    currency: draft.currency.trim().toUpperCase(),
    ...toUnitPrice(draft),
  }
}

const UNITS_KEY = ['units'] as const

/**
 * The overview, the series and the navigation's own gate are three reads of the
 * same rows, so a write that refreshed one of them would leave the page and the
 * tab strip disagreeing about what the household has.
 */
function useInvalidateUnits(): () => void {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: UNITS_KEY })
  }
}

export function useUnitPrices(key: string | null): UseQueryResult<UnitPrices> {
  return useQuery({
    queryKey: ['units', 'prices', key],
    queryFn: () => api.unitPrices(key ?? ''),
    enabled: key !== null,
  })
}

export function useDefineUnit(): UseMutationResult<UnitDefinition, Error, DefineUnitInput> {
  const invalidate = useInvalidateUnits()
  return useMutation({ mutationFn: api.defineUnit, onSuccess: invalidate })
}

export interface PricedUnit {
  key: string
  price: UnitPriceInput
}

export function useRecordUnitPrice(): UseMutationResult<UnitDefinition, Error, PricedUnit> {
  const invalidate = useInvalidateUnits()
  return useMutation({
    mutationFn: ({ key, price }: PricedUnit) => api.recordUnitPrice(key, price),
    onSuccess: invalidate,
  })
}

export interface DatedUnit {
  key: string
  asOf: string
}

export function useRemoveUnitPrice(): UseMutationResult<UnitDefinition, Error, DatedUnit> {
  const invalidate = useInvalidateUnits()
  return useMutation({
    mutationFn: ({ key, asOf }: DatedUnit) => api.removeUnitPrice(key, asOf),
    onSuccess: invalidate,
  })
}

export interface RenamedUnit {
  key: string
  label: string
}

export function useRenameUnit(): UseMutationResult<UnitDefinition, Error, RenamedUnit> {
  const invalidate = useInvalidateUnits()
  return useMutation({
    mutationFn: ({ key, label }: RenamedUnit) => api.renameUnit(key, label),
    onSuccess: invalidate,
  })
}

export function useRemoveUnit(): UseMutationResult<RemovedUnit, Error, string> {
  const invalidate = useInvalidateUnits()
  return useMutation({ mutationFn: api.removeUnit, onSuccess: invalidate })
}

/**
 * What a household should be told about a unit priced on one day.
 *
 * The page divides a moving net worth by the latest price at or before each
 * date, so a unit with one observation divides every day by the same figure and
 * draws the wealth back rather than what it buys. That is not wrong arithmetic,
 * which is exactly why it has to be said out loud.
 */
export function thinHistory(unit: { label: string; observations: number; firstDate: string }): string | null {
  if (unit.observations > 1) return null
  return `${unit.label} has a price on ${unit.firstDate} and on no other day, so every point on its line divides a changing net worth by the same unchanging figure. The shape is your wealth, not what it buys.`
}
